import moment from 'moment';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { getCampaignStatusHint } from '../../service/CampaignHelpers/CampaignHelpers';
import { DayBudgetValue, WalletDayBudgetValue } from '../../typings/budget';
import { LinkTarget } from '../../typings/common';
import { DailyBudgetLimit } from '../../typings/constants';
import { StatusInfo } from '../../typings/domain';
import {
    CurrencyCode,
    GdCampaignPrimaryStatus,
    GdCampaignPrimaryStatusDesc,
    GdWalletAction
} from '../../typings/graphql-grid';
import { ControlledDayBudgetEditPopup } from '../ControlledDayBudgetEditPopup/ControlledDayBudgetEditPopup';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';
import { StyledLink } from '../StyledLink/StyledLink';

import './GridCampaignStatusCell.css';

export interface GridCampaignStatusCellProps {
    name: string;
    statusInfo: StatusInfo;
    agencyId: string | null;
    campaignId: string;
    canPay: boolean;
    currency: CurrencyCode;
    isSaving: boolean;
    login: string;
    dayBudgetValue?: DayBudgetValue;
    saveDayBudget: (value: DayBudgetValue) => void;
    walletBudget?: WalletDayBudgetValue;
    dailyBudgetLimit: DailyBudgetLimit;
    columnName: string;
    error?: string;
    isGridScrolling: boolean;
    walletActions: Array<GdWalletAction>;
    isTextVisible: boolean;
    startDate: moment.Moment;
    endDate?: moment.Moment;
}

interface GridCampaignStatusCellState {
    isDayBudgetEditPopupOpened: boolean;
}

const BLOCK = 'grid-campaign-status-cell';

export class GridCampaignStatusCell extends React.Component<
    GridCampaignStatusCellProps,
    GridCampaignStatusCellState
> {
    componentDidUpdate(prevProps: GridCampaignStatusCellProps) {
        if (
            (prevProps.isSaving !== this.props.isSaving ||
                prevProps.error !== this.props.error) &&
            !this.props.isSaving &&
            !this.props.error
        ) {
            this.setState({
                isDayBudgetEditPopupOpened: false
            });
        }
    }

    constructor(props: GridCampaignStatusCellProps) {
        super(props);
        this.state = {
            isDayBudgetEditPopupOpened: false
        };
    }

    render() {
        const {
            name,
            statusInfo,
            campaignId,
            agencyId,
            login,
            canPay,
            isTextVisible,
            startDate,
            endDate
        } = this.props;
        const type = statusInfo.type;

        return (
            <div className={Bem.cls(BLOCK)}>
                <p
                    className={Bem.cls({
                        block: BLOCK,
                        elem: 'status',
                        mods: {
                            type: Bem.beminize(type)
                        }
                    })}
                >
                    {this.getStatusIcon()}
                    {isTextVisible && name}
                </p>

                {isTextVisible &&
                    this.getSignContent(
                        statusInfo,
                        campaignId,
                        login,
                        agencyId,
                        canPay,
                        startDate,
                        endDate
                    )}
            </div>
        );
    }

    private getStatusIcon() {
        const { name } = this.props;

        switch (this.props.statusInfo.type) {
            case GdCampaignPrimaryStatus.ACTIVE:
                return (
                    <Shape2
                        icon={Shape2Icon.statusRunning}
                        theme={Shape2Theme.light}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={name}
                    />
                );
            case GdCampaignPrimaryStatus.MODERATION_DENIED:
            case GdCampaignPrimaryStatus.NO_MONEY:
                return (
                    <Shape2
                        icon={Shape2Icon.statusStopped}
                        theme={Shape2Theme.light}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={name}
                    />
                );
            case GdCampaignPrimaryStatus.MODERATION:
            case GdCampaignPrimaryStatus.TEMPORARILY_PAUSED:
                return (
                    <Shape2
                        icon={Shape2Icon.statusPending}
                        theme={Shape2Theme.light}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={name}
                    />
                );
            case GdCampaignPrimaryStatus.STOPPED:
            case GdCampaignPrimaryStatus.DRAFT:
            case GdCampaignPrimaryStatus.ARCHIVED:
            default:
                return (
                    <Shape2
                        icon={Shape2Icon.statusEnded}
                        theme={Shape2Theme.light}
                        width={12}
                        height={12}
                        cls={Bem.cls(BLOCK, 'icon')}
                        title={name}
                    />
                );
        }
    }

    private getSignContent = (
        statusInfo: StatusInfo,
        campaignId: string,
        chiefLogin: string,
        agencyId: string | null,
        canPay: boolean,
        startDate: moment.Moment,
        endDate?: moment.Moment
    ) => {
        const { walletActions } = this.props;

        const hint = getCampaignStatusHint(
            statusInfo,
            campaignId,
            chiefLogin,
            agencyId,
            canPay,
            walletActions,
            startDate,
            endDate
        );

        if (!hint) {
            return null;
        }
        const isPausedByDayBudget =
            statusInfo.hintType ===
            GdCampaignPrimaryStatusDesc.IS_PAUSED_BY_DAY_BUDGET;

        if (hint.link === undefined && !isPausedByDayBudget) {
            return <p className={Bem.cls(BLOCK, 'hint')}>{hint.text}</p>;
        }

        const linkOpts = isPausedByDayBudget
            ? { onClick: this.showDayBudgetEditPopup }
            : { href: hint.link };

        const link = (
            <StyledLink
                cls={Bem.cls(BLOCK, 'hint-link')}
                {...linkOpts}
                target={LinkTarget.blank}
            >
                {hint.text}
                <Shape2
                    icon={Shape2Icon.link}
                    theme={Shape2Theme.light}
                    width={10}
                    height={10}
                    cls={Bem.cls(BLOCK, 'hint-link-icon')}
                />
                {}
            </StyledLink>
        );

        return (
            <p className={Bem.cls(BLOCK, 'hint')}>
                {this.getDayBudgetEditPopup(link)}
            </p>
        );
    };

    private getDayBudgetEditPopup(children: JSX.Element) {
        const {
            dayBudgetValue,
            saveDayBudget,
            campaignId,
            currency,
            dailyBudgetLimit,
            isSaving,
            walletBudget,
            error
        } = this.props;
        const { isDayBudgetEditPopupOpened } = this.state;

        return (
            <ControlledDayBudgetEditPopup
                value={dayBudgetValue}
                error={error}
                walletBudget={walletBudget}
                saveDayBudget={saveDayBudget}
                campaignId={campaignId}
                isSaving={isSaving}
                currency={currency}
                onCancel={this.hideDayBudgetEditPopup}
                onSaveSuccess={this.hideDayBudgetEditPopup}
                isPopupOpened={isDayBudgetEditPopupOpened}
                dailyBudgetLimit={dailyBudgetLimit}
            >
                {children}
            </ControlledDayBudgetEditPopup>
        );
    }

    private showDayBudgetEditPopup = () => {
        this.setState({
            isDayBudgetEditPopupOpened: true
        });
    };

    private hideDayBudgetEditPopup = () => {
        this.setState({
            isDayBudgetEditPopupOpened: false
        });
    };
}
